/**
 * **대시보드 그림 정본** — 브랜드 계열 화면의 대시보드 탭이 그리는 칸들.
 *
 * 칸은 두 갈래다. **붙박이 칸**(`metrics`·`trend`…)은 이 표가 이름과 차례만 알고 그림은
 * `MomentDashboard` 가 그린다. **만든 칸**(`custom:…`)은 사용자가 붙이는 칸이고, 그 정의가
 * 줄(`menuSets[].dashboard.custom`)에 함께 담긴다.
 *
 * 만든 칸은 다시 셋이다.
 *   `count`    기준 축과 기간을 정해 **몇 건인지 센다**
 *   `overview` 예전 화면 맨 위 띠에 박혀 있던 **요약 카드 넷** 중 하나
 *   `pivot`    행·열·지표·그림을 정해 **무엇이든 그린다**(막대·선·도넛·히트맵·순위·낱말 구름…)
 *
 * 🔑 **기본은 붙박이 다 켜짐, 만든 칸 없음이다** — 저장이 비면 브랜드 멘션 화면과 한 칸도
 *   다르지 않게 보인다. 사용자가 처음 빼거나 옮기거나 더하는 순간에만 문서에 적힌다.
 * ⚠️ **붙박이 열쇠를 바꾸면 이미 저장된 줄의 그 칸이 사라진다** — 읽을 때 모르는 열쇠를
 *   떨구기 때문이다. 이름(`label`)은 마음대로 고쳐도 되지만 `id` 는 못 고친다.
 * ⚠️ **여기 더한 붙박이 열쇠는 `MomentDashboard` 에도 같은 이름의 칸이 있어야 한다** — 한쪽만
 *   더하면 그 줄은 편집 화면에 뜨지만 아무것도 안 그려진다. 예외는 `negative` 하나다 — 그림을
 *   화면이 그려 넘긴다(`widgetExtraNodes.negative`)라 안 넘기는 화면(검색·자사 계정)에는 안 선다.
 * 🚨 **만든 칸의 열쇠는 `custom:` 으로 시작한다** — 붙박이와 섞이지 않게 하는 유일한 표시다.
 */

/** 붙박이 칸 — 그림이 코드에 박혀 있다 */
export type BuiltinDashboardWidgetId =
  | 'metrics'
  | 'negative'
  | 'trend'
  | 'matrix'
  | 'rankings'
  | 'top';

/** 사용자가 만든 셈 칸 — 정의는 줄이 담는다 */
export type CustomDashboardWidgetId = `custom:${string}`;

/** 대시보드가 그리는 칸 하나 */
export type DashboardWidgetId = BuiltinDashboardWidgetId | CustomDashboardWidgetId;

export type DashboardWidgetSpec = {
  id: BuiltinDashboardWidgetId;
  label: string;
  /** 편집 화면에서 「무엇이 그려지나」를 한 줄로 알린다 */
  description: string;
};

/** 차례의 기본값 — 브랜드 멘션 화면이 지금 그리는 순서 그대로다 */
export const DASHBOARD_WIDGETS: DashboardWidgetSpec[] = [
  {
    id: 'metrics',
    label: '요약 숫자',
    description: '총 멘션 수 · 조회수 · 반응수 · 반응률 네 장',
  },
  {
    id: 'trend',
    label: '멘션 추이',
    description: '날짜별 멘션량 — 전체·채널별, 언급량·도달수치를 골라 본다',
  },
  {
    id: 'matrix',
    label: '히트맵 · Mix',
    description: '브랜드 × 채널/국가 히트맵과 채널·국가 비중',
  },
  {
    id: 'rankings',
    label: '연관 랭킹 Top 10',
    description: '해시태그 · 브랜드 · 피부고민 · 성분',
  },
  {
    id: 'top',
    label: 'Top postings · accounts',
    description: '채널별 인기 게시글과 인플루언서 랭킹',
  },
  // 맨 끝 — 한 줄에 한 건씩 접힌 목록이라 차트들 아래에 둔다(저장된 배치는 그 차례를 따른다)
  {
    id: 'negative',
    label: '부정 멘션',
    description: '조회 조건 안에서 부정 문장이 붙은 글 — 최근 것부터 한 줄씩, 원문/번역',
  },
];

export const DASHBOARD_WIDGET_IDS: BuiltinDashboardWidgetId[] = DASHBOARD_WIDGETS.map((row) => row.id);

const WIDGET_BY_ID = new Map(DASHBOARD_WIDGETS.map((row) => [row.id, row]));

// ── 만든 칸 ────────────────────────────────────────────────────────────────

/**
 * 셈 칸이 걸 수 있는 **축** — 브랜드 멘션 조회가 쥔 판정 스위치 셋 그대로다.
 * 🔑 화면의 다른 조건(채널·국가·언어·최소 도달)은 **화면이 좁혀 둔 것을 따른다** — 칸마다
 *   따로 고르게 하면 같은 대시보드 안에서 서로 다른 모집단의 수가 나란히 서게 된다.
 */
export const DASHBOARD_COUNT_AXES = [
  { key: 'beautyEval', label: 'AI 뷰티 필터' },
  { key: 'canonicalBrandOnly', label: '브랜드 인식 기준' },
  { key: 'linkSearch', label: '링크 내 단어 포함' },
] as const;

export type DashboardCountAxis = (typeof DASHBOARD_COUNT_AXES)[number]['key'];

/** 셀 수 있는 구간(일) — 화면이 「최근 N일」로 적는다 */
export const DASHBOARD_COUNT_DAYS = [1, 7, 30, 90] as const;

export type DashboardCountDays = (typeof DASHBOARD_COUNT_DAYS)[number];

/**
 * 셈 칸의 **모양**.
 * ⚠️ `ratio`·`bar` 는 **전체 수를 한 번 더 묻는다**(축을 하나도 안 걸고) — 분모가 있어야
 *   비율이 서기 때문이다. `stat` 은 한 번만 묻는다.
 */
export const DASHBOARD_COUNT_DESIGNS = [
  { key: 'stat', label: '큰 숫자', description: '수 하나만 크게' },
  { key: 'ratio', label: '숫자 + 비율', description: '수와 전체 대비 비율' },
  { key: 'bar', label: '비교 막대', description: '전체와 나란히 놓은 막대' },
] as const;

export type DashboardCountDesign = (typeof DASHBOARD_COUNT_DESIGNS)[number]['key'];

/** 사용자가 만든 셈 칸 한 벌 — 줄의 `dashboard.custom` 에 담긴다 */
export type DashboardCountWidget = {
  id: CustomDashboardWidgetId;
  kind: 'count';
  /** 한 줄에서 차지하는 몫 — `DASHBOARD_WIDGET_SPANS` */
  span?: DashboardWidgetSpan;
  /** 칸 머리에 적는 이름 */
  label: string;
  days: DashboardCountDays;
  /** 켤 축 — 빈 배열이면 「아무 기준도 안 건 전체」다 */
  axes: DashboardCountAxis[];
  design: DashboardCountDesign;
};

// ── 오버뷰 카드 ────────────────────────────────────────────────────────────

/**
 * 예전 화면 맨 위 띠에 박혀 있던 **요약 카드 넷**.
 *
 * 🚨 **기간은 카드마다 정해져 있다** — 고르는 자리가 아니다. 조회수·반응은 화면의 조회기간을,
 *   하이라이트는 서버가 제 창(7일)으로 갈아 끼우고, 부정 멘션은 최근 24시간 샘플이다.
 *   기간 고르개를 달면 안 먹는 값을 고르게 된다.
 * ⚠️ **화면의 사이드바 필터에 반응하는 정도가 카드마다 다르다** — 조회수·반응은 반응하고,
 *   7일 하이라이트는 반응하지 않는다(서버가 제 조건으로 센다).
 */
export const DASHBOARD_OVERVIEW_CARDS = [
  {
    key: 'views',
    label: '총 조회수',
    description: '조회기간의 조회수 합계와 채널별 쪼개기',
  },
  {
    key: 'metrics',
    label: '총 반응',
    description: '조회기간의 조회·댓글·좋아요 합계와 채널별 쪼개기',
  },
  {
    key: 'highlight',
    label: '최근 7일 하이라이트',
    description: '최근 7일 멘션·증감 · 최다 국가 · 최다 채널 · 상위 해시태그(기간 고정)',
  },
  {
    key: 'negative',
    label: '부정 멘션',
    description: '최근 24시간 샘플에서 부정 반응이 달린 글과 그 낱말',
  },
] as const;

export type DashboardOverviewCard = (typeof DASHBOARD_OVERVIEW_CARDS)[number]['key'];

/**
 * 오버뷰 카드의 **모양**.
 * 🔑 `compact` 는 **채널별 쪼개기와 칩을 접는다** — 대시보드에 여럿 세울 때 띠 하나에 다 담긴다.
 */
export const DASHBOARD_OVERVIEW_DESIGNS = [
  { key: 'card', label: '카드 그대로', description: '띠에 있던 모양 그대로' },
  { key: 'compact', label: '한 줄 요약', description: '쪼개기를 접고 핵심 수만' },
] as const;

export type DashboardOverviewDesign = (typeof DASHBOARD_OVERVIEW_DESIGNS)[number]['key'];

export type DashboardOverviewWidget = {
  id: CustomDashboardWidgetId;
  kind: 'overview';
  /** 한 줄에서 차지하는 몫 — `DASHBOARD_WIDGET_SPANS` */
  span?: DashboardWidgetSpan;
  label: string;
  card: DashboardOverviewCard;
  design: DashboardOverviewDesign;
};

// ── 피벗 칸 ────────────────────────────────────────────────────────────────

/**
 * **행·열·지표·그림을 직접 정하는 칸** — 축과 그림 종류의 정본은 `constants/pivot-fields` ·
 * `constants/pivot-charts` 다(예전 분석 화면이 쓰던 것 그대로).
 *
 * 🔑 **열 축은 비울 수 있다**(`''`) — 비우면 행이 가로축이 되고 계열이 하나다. 누적 막대·선·
 *   영역·히트맵처럼 열이 있어야 뜻이 있는 그림은 화면이 「열을 골라 주세요」라고 적는다.
 * 🔑 **기간 `0` 은 「화면의 조회기간을 따른다」**다 — 붙박이 차트들과 같은 기간이라 한 판에
 *   놓고 견줄 수 있다. 다른 수를 고르면 그 칸만 제 창으로 센다(칸에 그렇게 적힌다).
 */
export type DashboardPivotWidget = {
  id: CustomDashboardWidgetId;
  kind: 'pivot';
  /** 한 줄에서 차지하는 몫 — `DASHBOARD_WIDGET_SPANS` */
  span?: DashboardWidgetSpan;
  label: string;
  /** 무엇으로 나눌 것인가 — `ANALYTICS_AXES` 의 열쇠 */
  rowAxis: string;
  /** 두 번째 축 — 빈 문자열이면 안 나눈다 */
  columnAxis: string;
  /** 무엇을 셀 것인가 — `ANALYTICS_METRICS` 의 열쇠 */
  metric: string;
  /** 어떤 그림으로 — `ANALYTICS_CHARTS` 의 열쇠 */
  chart: string;
  /** 며칠치 — `0` 이면 화면의 조회기간 */
  days: number;
};

/** 피벗 칸이 고를 수 있는 기간 — `0` 은 「화면 기간 따름」이다 */
export const DASHBOARD_PIVOT_DAYS = [0, 7, 30, 90] as const;

export function dashboardPivotDaysLabel(days: number): string {
  return days > 0 ? `최근 ${days}일` : '화면 기간 따름';
}

// ── 배치 ──────────────────────────────────────────────────────────────────

/**
 * 칸이 **한 줄에서 차지하는 몫** — 한 줄을 넷으로 나눈 칸 수다.
 *
 * ```
 *   1  넷이 한 줄        2  둘이 한 줄
 *   3  넷 중 셋          4  혼자 한 줄(기본)
 * ```
 *
 * 🔑 **차례가 곧 배치다** — 앞에서부터 채우고 한 줄이 차면 다음 줄로 넘어간다. 좌표를 따로
 *   쥐면 칸 하나를 지울 때마다 빈 자리가 남는다.
 * ⚠️ **좁은 화면에서는 전부 한 줄 하나**다 — 폰에서 넷을 나란히 놓으면 아무것도 안 읽힌다.
 * ⚠️ 붙박이 칸은 늘 혼자 한 줄이다 — 그림이 그 폭을 기대고 짜여 있다.
 */
export const DASHBOARD_WIDGET_SPANS = [
  { key: 4, label: '한 줄 전체' },
  { key: 3, label: '3/4' },
  { key: 2, label: '1/2' },
  { key: 1, label: '1/4' },
] as const;

export type DashboardWidgetSpan = (typeof DASHBOARD_WIDGET_SPANS)[number]['key'];

/** 한 줄을 몇으로 나누나 — 「최대 넷」의 그 넷이다 */
export const DASHBOARD_ROW_UNITS = 4;

export const DEFAULT_WIDGET_SPAN: DashboardWidgetSpan = 4;

/** 저장본에서 읽은 몫 — 모르는 값이면 한 줄 전체다(칸을 버리지 않는다) */
export function toDashboardWidgetSpan(value: unknown): DashboardWidgetSpan {
  const span = Number(value);
  return (DASHBOARD_WIDGET_SPANS as readonly { key: number }[]).some((row) => row.key === span)
    ? (span as DashboardWidgetSpan)
    : DEFAULT_WIDGET_SPAN;
}

/** 사용자가 만든 칸 — 갈래를 `kind` 로 가른다 */
export type DashboardCustomWidget =
  | DashboardCountWidget
  | DashboardOverviewWidget
  | DashboardPivotWidget;

/**
 * 아직 열쇠가 안 붙은 칸 — 대화상자가 내는 것.
 * 🚨 `Omit<…, 'id'>` 를 union 에 바로 쓰면 **공통 칸만 남는다**(TS 규약) — 갈래마다 따로 벗긴다.
 */
export type NewDashboardCustomWidget =
  | Omit<DashboardCountWidget, 'id'>
  | Omit<DashboardOverviewWidget, 'id'>
  | Omit<DashboardPivotWidget, 'id'>;

/** 열쇠를 붙인다 — 갈래를 좁혀 붙여야 union 이 안 뭉개진다 */
export function withCustomDashboardWidgetId(
  widget: NewDashboardCustomWidget,
  id: CustomDashboardWidgetId,
): DashboardCustomWidget {
  if (widget.kind === 'overview') return { ...widget, id };
  if (widget.kind === 'pivot') return { ...widget, id };
  return { ...widget, id };
}

const OVERVIEW_BY_KEY = new Map(DASHBOARD_OVERVIEW_CARDS.map((row) => [row.key, row]));

export function dashboardOverviewCardLabel(card: string): string {
  return OVERVIEW_BY_KEY.get(card as DashboardOverviewCard)?.label ?? card;
}

const AXIS_LABEL = new Map<string, string>(DASHBOARD_COUNT_AXES.map((row) => [row.key, row.label]));

/** 축 목록 → 사람이 읽는 조건 한 줄 */
export function dashboardCountAxisLabel(axes: readonly string[]): string {
  const rows = axes.map((key) => AXIS_LABEL.get(key)).filter(Boolean);
  return rows.length > 0 ? rows.join(' · ') : '기준 없음(전체)';
}

export function isCustomDashboardWidgetId(value: unknown): value is CustomDashboardWidgetId {
  return typeof value === 'string' && value.startsWith('custom:') && value.length > 'custom:'.length;
}

export function isBuiltinDashboardWidgetId(value: unknown): value is BuiltinDashboardWidgetId {
  return typeof value === 'string' && WIDGET_BY_ID.has(value as BuiltinDashboardWidgetId);
}

/** 모르는 열쇠인가 — 저장본을 읽을 때 떨구는 판정 */
export function isDashboardWidgetId(value: unknown): value is DashboardWidgetId {
  return isBuiltinDashboardWidgetId(value) || isCustomDashboardWidgetId(value);
}

/**
 * 새 셈 칸의 열쇠 — **그 줄 안에서만 유일하면 된다**.
 * ⚠️ 같은 밀리초에 둘을 만들면 부딪히므로 이미 있는 열쇠를 피해 하나씩 올린다.
 */
export function nextCustomDashboardWidgetId(
  taken: readonly string[],
  at: number = Date.now(),
): CustomDashboardWidgetId {
  const used = new Set(taken);
  let seq = at;
  while (used.has(`custom:${seq}`)) seq += 1;
  return `custom:${seq}`;
}

/** 칸 이름 — 만든 칸은 그 정의가 이름을 쥔다 */
export function dashboardWidgetLabel(
  id: DashboardWidgetId,
  custom: readonly DashboardCustomWidget[] = [],
): string {
  if (isCustomDashboardWidgetId(id)) {
    return custom.find((row) => row.id === id)?.label || '내 위젯';
  }
  return WIDGET_BY_ID.get(id)?.label ?? id;
}
